/**
 * GetFilters Endpoint
 *
 * Read-only per-hotel availability metadata: the possible arrival days
 * (`Filter=CheckInDates`) and the lengths of stay (`Filter=Duration`) for one
 * product and occupancy. One request carries exactly one `<Filter>`.
 * Findings: `claude/specs/PDPCalendarRestriction/FINDINGS.md`.
 */

import { GetFiltersResultSchema, type GetFiltersResult } from '@/types/schemas';
import { PlayerApiError } from '@/types/errors';
import { playerClient } from './playerClient';
import { normalizeToArray } from '@/services/mappers/arrayNormalizers';
import {
  buildFiltersPeriodXml,
  buildPassengersXml,
  buildRequestHeader,
  buildRoomsXml,
} from '@/utils/xml/xmlBuilder';
import type { PlayerOccupancy } from '@/utils/offers/occupancyToPlayerPax';
import { PLAYER_API_CONFIG } from '@/constants/player/config';

const { NAMESPACE } = PLAYER_API_CONFIG;

const ACTION = 'BewotecHubService/GetFilters';
const MASK_PATTERN = /<TravelStartDates>([01]+)<\/TravelStartDates>/;
const MASK_FROM_PATTERN = /<TravelStartDatesFrom>(\d{4}-\d{2}-\d{2})/;
const DURATION_ATTRIBUTE_PATTERN = /^Durations\.(\d+)$/;

/** Same occupancy shape GetProductOffers is built from (see `occupancyToPlayerPax`). */
export interface GetFiltersParams extends PlayerOccupancy {
  productCode: string;
}

export interface CheckInDates {
  /** First day the mask covers, `YYYY-MM-DD`. */
  from: string;
  /** One char per day from `from`, `'1'` = arrival possible. */
  mask: string;
}

type GetFiltersFilter = 'CheckInDates' | 'Duration';

function createGetFiltersRequest(
  params: GetFiltersParams,
  filter: GetFiltersFilter,
  start: string,
  postOffsetDays: number
): string {
  const passengersXml = buildPassengersXml({
    adults: params.adults,
    children: params.children,
    passengers: params.passengers,
  });

  return `
<soapenv:Envelope xmlns:soapenv="http://schemas.xmlsoap.org/soap/envelope/"
                  xmlns:bewo="${NAMESPACE}">
   <soapenv:Header/>
   <soapenv:Body>
      <bewo:GetFilters>
         <bewo:GetFiltersRequest>
            ${buildRequestHeader('')}
            <bewo:Accommodation>
               <bewo:ProductCodes>
                  <bewo:ProductCode>${params.productCode}</bewo:ProductCode>
               </bewo:ProductCodes>
               ${buildRoomsXml(params.rooms)}
            </bewo:Accommodation>
            <bewo:Passengers>${passengersXml}
            </bewo:Passengers>
            ${buildFiltersPeriodXml(start, postOffsetDays)}
            <bewo:Filter>${filter}</bewo:Filter>
         </bewo:GetFiltersRequest>
      </bewo:GetFilters>
   </soapenv:Body>
</soapenv:Envelope>`;
}

/**
 * Sends one GetFilters request and returns the raw XML plus the validated result.
 * `SoapClient` only throws on SOAP faults, so `Success=false` is turned into an
 * error here; otherwise it would read as "nothing sold" for this occupancy.
 */
async function callGetFilters(
  body: string,
  signal?: AbortSignal
): Promise<{ raw: string; result: GetFiltersResult }> {
  const response = await playerClient.call({ action: ACTION, body, signal });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const parsed = response.parsed as any;
  const validation = GetFiltersResultSchema.safeParse(
    parsed?.Envelope?.Body?.GetFiltersResponse?.GetFiltersResult
  );

  if (!validation.success) {
    throw new PlayerApiError(
      'Invalid GetFilters response',
      'INVALID_RESPONSE',
      ACTION,
      response.raw
    );
  }
  if (!validation.data.Success) {
    throw new PlayerApiError(
      'GetFilters returned Success=false',
      'API_ERROR',
      ACTION,
      response.raw
    );
  }

  return { raw: response.raw, result: validation.data };
}

/**
 * Arrival-day bitmap for one product and occupancy (`Filter=CheckInDates`).
 *
 * The mask window is fixed by the gateway (from tomorrow, ~500 days); `start`
 * only has to be present. Returns `null` when nothing is sold for this occupancy.
 */
export async function getCheckInDates(
  params: GetFiltersParams & { start: string },
  options?: { signal?: AbortSignal }
): Promise<CheckInDates | null> {
  const { raw } = await callGetFilters(
    createGetFiltersRequest(params, 'CheckInDates', params.start, 0),
    options?.signal
  );

  const mask = raw.match(MASK_PATTERN)?.[1];
  const from = raw.match(MASK_FROM_PATTERN)?.[1];

  return mask && from ? { from, mask } : null;
}

/**
 * Lengths of stay in nights (`Filter=Duration`) for arrivals in
 * `[arrival, arrival + postOffsetDays]`, ascending and unique. With
 * `postOffsetDays = 0` these are exactly the stays starting on `arrival`.
 */
export async function getDurations(
  params: GetFiltersParams & { arrival: string; postOffsetDays: number },
  options?: { signal?: AbortSignal }
): Promise<number[]> {
  const { result } = await callGetFilters(
    createGetFiltersRequest(params, 'Duration', params.arrival, params.postOffsetDays),
    options?.signal
  );

  const nights = normalizeToArray(result.Attributes?.Attribute)
    .map((entry) => entry.Attribute?.match(DURATION_ATTRIBUTE_PATTERN)?.[1])
    .filter((value): value is string => value !== undefined)
    .map(Number);

  return [...new Set(nights)].sort((a, b) => a - b);
}
